import { Component, EventEmitter, Input, Output, ViewChild, ElementRef, ChangeDetectorRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { EditorModule } from 'primeng/editor';
import { HttpClient, HttpClientModule, HttpResponse } from '@angular/common/http'; 
import { ManageAssessmentApiService } from "../../../../../core/services/api/manage-assessment-api.service";

import { FormsModule } from '@angular/forms';

@Component({
  selector: "app-manage-assessment-upload-popup",
  standalone: true,
  imports: [ CommonModule, DialogModule, ButtonModule, EditorModule, HttpClientModule, FormsModule ],
  templateUrl: "./manage-assessment-upload-popup.component.html",
  styleUrls: ["./manage-assessment-upload-popup.component.scss"],
})
export class ManageAssessmentUploadPopupComponent implements OnInit {
  @Input() visible: boolean = false;
  @Output() closeDialog = new EventEmitter<void>();
  @Output() saveDraft = new EventEmitter<void>();
  @Output() submitForm = new EventEmitter<void>();
  @Output() uploadFile = new EventEmitter<File>();
  @ViewChild("fileInput") fileInput!: ElementRef; // Reference to the hidden file input

  selectedFile: File | null = null;
  fileDescription: string = "";
  uploadError: string | null = null;
  isProcessing: boolean = false;

  // Quill editor configuration with image paste support
  // Note: Quill natively supports image paste via clipboard
  editorModules: any = {};

  private readonly MAX_FILE_SIZE_MB = 5;
  private readonly ALLOWED_FILE_TYPES = [
    "text/csv",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ];

  constructor(
    private readonly http: HttpClient,
    private readonly manageAssessmentApiService: ManageAssessmentApiService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Initialize editor modules
    // Quill natively supports image paste from clipboard and drag-drop
    this.editorModules = {
      toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean']
      ],
      clipboard: {
        matchVisual: false
      }
    };
  }

  onClose(): void {
    this.resetUploadState();
    this.closeDialog.emit();
  }

  downloadTemplate(event: Event): void {
    event.preventDefault(); // Prevent default link behavior

    this.isProcessing = true; // Disable buttons during download

    // Modify the API call to get the full HttpResponse, including headers
    this.manageAssessmentApiService.downloadRcaTemplate().subscribe({
      next: (response: HttpResponse<Blob>) => {
        const blob = response.body;
        if (!blob) {
          console.error("No blob data received for download.");
          alert("Failed to download template: No file data.");
          this.isProcessing = false;
          return;
        }
        let filename = 'RCA_Form_Template.xlsx';
        // Attempt to extract filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-\d['"]*)?([^;\n]*?)['"]?;?/i);
          if (filenameMatch && filenameMatch[1]) {
            try {
              // Decode URI component to handle potential URL-encoded characters
              filename = decodeURIComponent(filenameMatch[1].replace(/^UTF-8''/, ''));
            } catch (e) {
              console.warn('Could not decode filename from Content-Disposition, using raw:', filenameMatch[1]);
              filename = filenameMatch[1];
            }
          }
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.isProcessing = false;
         this.cdr.detectChanges();
      },
      error: (error) => {
        console.error("Error downloading template:", error);
        alert("Failed to download template. Please try again later.");
        this.isProcessing = false;
      },
    });
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
    this.uploadError = null;
  }

  /**
   * Get the plain text length from the HTML content (excluding HTML tags)
   * This helps with character count validation
   */
  getPlainTextLength(): number {
    if (!this.fileDescription) return 0;
    
    // Create a temporary div to strip HTML tags
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = this.fileDescription;
    const plainText = tempDiv.textContent || tempDiv.innerText || '';
    return plainText.trim().length;
  }

  onFileSelected(event: Event): void {
    const element = event.target as HTMLInputElement;
    const file = element.files ? element.files[0] : null;

    if (file) {
      this.selectedFile = file;
      this.validateFile(file);
    } else {
      this.selectedFile = null;
      this.uploadError = null;
    }
    element.value = "";
  }

  private validateFile(file: File): void {
    this.uploadError = null;

    const maxSizeBytes = this.MAX_FILE_SIZE_MB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      this.uploadError = `File size exceeds the maximum limit of ${this.MAX_FILE_SIZE_MB}MB.`;
      this.selectedFile = null;
      return;
    }

    if (!this.ALLOWED_FILE_TYPES.includes(file.type)) {
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      if (!["csv", "xlsx"].includes(fileExtension || "")) {
        this.uploadError =
          "Invalid file type. Only .csv and .xlsx files are allowed.";
        this.selectedFile = null;
        return;
      }
    }
  }

  // --- API Call Logic ---
  private async sendFileToApi(action: "submit" | "draft"): Promise<void> {
    if (!this.selectedFile) {
      alert("Please select a file to upload.");
      return;
    }
    if (this.uploadError) {
      alert(`Cannot ${action}: ${this.uploadError}`);
      return;
    }
    this.isProcessing = true;
    try {
      // Create FormData object
    const formData = new FormData();
    // 'file' is the key expected by the API for the file itself
    formData.append('file', this.selectedFile);
      if (action === 'submit') {
        this.uploadRcaSubmitMethod(formData);
      } else if (action === 'draft') {
        this.uploadRcaDraftMethod(formData);
      }
    } catch (error) {
      console.error('Error processing file for upload:', error);
      alert('Failed to prepare file for upload. Please try again.');
      this.isProcessing = false;
    }
  }

  uploadRcaSubmitMethod(payload: FormData) {
    // Call your API service
    this.manageAssessmentApiService.uploadRcaSubmit(payload).subscribe({
      next: (response) => {
        this.onClose();
      },
      error: (error) => {
        this.isProcessing=false
        this.cdr.detectChanges();
      },
      complete: () => {
        this.isProcessing = false; // Re-enable buttons
      },
    });
  }

  uploadRcaDraftMethod(payload: FormData) {
    // Call your API service
    this.manageAssessmentApiService.uploadRcaDraft(payload).subscribe({
      next: (response) => {
        this.onClose();
      },
      error: (error) => {
        this.isProcessing = false;
        this.cdr.detectChanges();
      },
      complete: () => {
        this.isProcessing = false; // Re-enable buttons
      },
    });
  }

  onUploadClick(): void {
    if (this.selectedFile && !this.uploadError) {
      alert(
        `File "${this.selectedFile.name}" is ready for upload with description: "${this.fileDescription}". (Simulated upload)`
      );
    } else if (this.uploadError) {
      alert(`Cannot upload: ${this.uploadError}`);
    } else {
      alert("Please select a file to upload.");
    }
  }

  saveAsDraft(): void {
    this.sendFileToApi("draft");
  }

  onSubmit(): void {
    this.sendFileToApi("submit");
  }

  // Resets the upload-related state variables
  private resetUploadState(): void {
    this.selectedFile = null;
    this.fileDescription = "";
    this.uploadError = null;
    this.isProcessing = false;
  }
}